/* eslint-disable @typescript-eslint/no-explicit-any */
import { AttributeKey, DatableLog, DatableTrace, SimulateChain, TelemetryEvent } from '@ui/interfaces'
import dayjs from 'dayjs'

export const getNestedKeys = (obj: any, parentKey = '') => {
  let keys: string[] = []

  for (const key in obj) {
    const currentKey = parentKey ? `${parentKey}.${key}` : key

    if (typeof obj[key] === 'object') {
      keys = keys.concat(getNestedKeys(obj[key], currentKey))
    } else {
      keys.push(currentKey)
    }
  }

  return keys
}

export const extractUniqueKeys = (
  data: Array<TelemetryEvent<DatableLog | DatableTrace> | SimulateChain | any>,
  type: 'input' | 'panelInput' | 'output' | 'data'
): AttributeKey[] => {
  const keySet = new Set()
  data.forEach((obj) => {
    if (obj === null) return

    switch (type) {
      case 'data':
        if ('record' in obj) {
          const keys = getNestedKeys(obj.record)
          keys.forEach((key) => {
            keySet.add(key)
          })
        } else {
          const keys = getNestedKeys(obj)
          keys.forEach((key) => {
            keySet.add(key)
          })
        }
        break
      case 'panelInput':
      case 'input':
        if ('input' in obj) {
          const keys = getNestedKeys(obj.input)
          keys.forEach((key) => {
            keySet.add(key)
          })
        }
        break

      case 'output':
        if ('output' in obj) {
          const keys = getNestedKeys(obj.output)
          keys.forEach((key) => {
            keySet.add(key)
          })
        }
        break
    }
  })

  return Array.from(keySet) as AttributeKey[]
}

// Keys can be nested and the nested keys are separated by a dot but we only need the first
// seperated key val as an accessor so we need to split the key and get the first element
// i.e. resource.service.name -> resource
// TODO: Might need to revisit this to handle different nested keys/turn into helper function
// How we handle most Telemetry Data
export const parseCellValue = ({
  key,
  obj,
  isTelemetryEvent,
}: {
  key: string
  obj: any
  isTelemetryEvent: boolean
}) => {
  // Split the key on dots and brackets, while keeping bracketed parts intact
  const path = key.match(/([^[.\]]+)|(\[([^[\]]+)\])/g)?.map((part) => part.replace(/^\[|\]$/g, '')) || []

  // Telemetry Events have a record key that we need to traverse
  // Otherwise we just traverse the object directly
  if (isTelemetryEvent) {
    return path.reduce((acc, curr) => (acc && acc[curr] !== undefined ? acc[curr] : undefined), obj.record)
  }

  const getValue = (currentObj: any, currentPath: string[]): any => {
    if (currentObj === null || currentObj === undefined || currentPath.length === 0) {
      return currentObj
    }
    const [head, ...tail] = currentPath
    return getValue(currentObj[head], tail)
  }

  const value = getValue(obj, path)

  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  if (key === 'timestamp') {
    return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
  }

  return value
}

export const getAttributeValues = (
  events: Array<TelemetryEvent<DatableLog | DatableTrace> | SimulateChain | any>,
  attributeKey: string
): Set<string> => {
  const values = new Set<string>()

  for (const event of events) {
    // Parse the attribute path (handles nested keys like 'resource[service.name]')
    const path = attributeKey.match(/([^[.\]]+)|(\[([^[\]]+)\])/g)?.map((part) => part.replace(/^\[|\]$/g, '')) || []

    // Navigate through the object using the path
    let value = path.reduce((acc, curr) => {
      return acc && typeof acc === 'object' ? acc[curr] : undefined
    }, event.record)

    // Add the value if it exists and can be converted to string
    if (value !== undefined && value !== null) {
      // Handle different value types
      if (typeof value === 'object') {
        value = JSON.stringify(value)
      } else {
        value = String(value)
      }
      values.add(value)
    }
  }

  return values
}
